/**
 * Phép tính thuần của màn Soạn thảo văn bản, chuyển từ `useSoanThao.ts` và `docFrame.ts` của foxainative:
 * dọn tên tệp làm trích yếu, chuẩn hoá giá trị trường ngày, ghép bản xem trước của mẫu điền trường, kiểm
 * thể thức bắt buộc, tệp `.doc` xuất văn bản. Không chạm DOM, không gọi HTTP.
 */
import { phanDau, phanKy } from './khung-van-ban.ts';
import { thoatHtml } from './mau-van-ban.ts';
import type { TheThucNhap } from './mau-van-ban.ts';
import type { TruongMau } from '../types.ts';

/** Giới hạn của backend (20MB mỗi tệp). */
export const TEP_TOI_DA = 20 * 1024 * 1024;
const DUOI_NHAN = /\.(txt|md|pdf|docx)$/i;
const DUOI_CHU = /\.(txt|md)$/i;

export function duoiDuocNhan(ten: string): boolean {
  return DUOI_NHAN.test(ten);
}

/** `.txt`/`.md` đọc ngay ở trình duyệt; PDF/DOCX nhờ backend trích chữ. */
export function docTaiCho(ten: string): boolean {
  return DUOI_CHU.test(ten);
}

/**
 * Tên tệp thô ("HUONG_DAN_SU_DUNG_Giao_viec(1).pdf") gửi làm `source_title`, backend dùng làm trích yếu:
 * bỏ đuôi, hậu tố "(1)", gạch dưới.
 */
export function lamSachTieuDe(tenTep: string): string {
  return (
    tenTep
      .replace(/\.[^.]+$/, '')
      .replace(/\s*\(\d+\)\s*$/, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || tenTep
  );
}

const NGAY_ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/** ISO (`<input type="date">`) → dd/mm/yyyy; khớp `_fmt_date` của backend. Giá trị lạ giữ nguyên. */
export function ngayHienThi(v: string): string {
  const m = NGAY_ISO.exec(v.trim());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
}

/** Chỉ trường `DATE` đổi dạng hiển thị; khớp `_display_values` của backend. */
export function giaTriHienThi(values: Record<string, string>, fields: TruongMau[] | null | undefined): Record<string, string> {
  const ngay = new Set((fields ?? []).filter((f) => f.type === 'DATE').map((f) => f.key));
  if (ngay.size === 0) return values;
  const ra: Record<string, string> = {};
  for (const [k, v] of Object.entries(values)) ra[k] = ngay.has(k) && v ? ngayHienThi(v) : v;
  return ra;
}

/**
 * Bản xem trước tức thì của mẫu điền trường: thay `{{key}}` bằng giá trị đang nhập (đã thoát HTML, xuống
 * dòng thành `<br>`), ghép khung NĐ30 khi có cơ quan hoặc chọn Quốc hiệu căn giữa — cùng cách backend ghép.
 */
export function xemTruocDienTruong(
  than: string,
  values: Record<string, string>,
  fields: TruongMau[] | null | undefined,
  theThuc: TheThucNhap,
  ten: string,
  kyHieuMau: string,
  homNay = new Date(),
): string {
  const hien = giaTriHienThi(values, fields);
  const ruot = than.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, k: string) => thoatHtml(hien[k] ?? '').replace(/\n/g, '<br>'));
  if (theThuc.cq.trim() || theThuc.cqch.trim() || theThuc.headerCenter) {
    return phanDau(ten, kyHieuMau, theThuc, homNay) + ruot + phanKy(theThuc);
  }
  return ruot;
}

/** Thể thức bắt buộc với mẫu AI soạn, theo thứ tự hiện lỗi. */
export const THE_THUC_BAT_BUOC = ['cqch', 'cq', 'so', 'kyhieu', 'dd', 'ky', 'cv'] as const;
export type KhoaTheThuc = (typeof THE_THUC_BAT_BUOC)[number];

export function theThucThieu(info: TheThucNhap): KhoaTheThuc[] {
  return THE_THUC_BAT_BUOC.filter((k) => !info[k].trim());
}

/** Nhãn các trường bắt buộc chưa điền của mẫu điền trường. */
export function truongBatBuocThieu(fields: TruongMau[] | null | undefined, values: Record<string, string>): string[] {
  return (fields ?? []).filter((f) => f.required && !(values[f.key] ?? '').trim()).map((f) => f.label);
}

/** Nguồn gửi AI: ưu tiên bản tóm tắt, không có thì văn bản gốc. */
export function noiDungNguon(nguon: { summary: string; original: string } | null): string {
  return (nguon?.summary || nguon?.original || '').trim();
}

/** Tệp Word (HTML) của văn bản đã soạn, như `exportDoc` của bản cũ. */
export function docXuatSoan(html: string): string {
  return `<html xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"></head><body style="font-family:'Times New Roman',serif;font-size:13pt;line-height:1.5;">${html}</body></html>`;
}

export function tenTepSoan(tenMau: string | null | undefined, homNay = new Date()): string {
  return `${tenMau ?? 'Van ban'} - FoxAI ${homNay.toISOString().slice(0, 10)}.doc`;
}
